import { api } from "./index";
import { toFrontStatus } from "./admin/status";

// Result item representing a collaboration/image entry for public search
export interface SearchResultItem {
  colaboracao_id: number;
  nome_especie: string;
  pais?: string;
  regiao?: string;
  estado?: string;
  municipio?: string;
  data?: string;
  latitude?: string;
  longitude?: string;
  altitude?: string;
  coletor?: string;
  instituicao_coletor?: string;
  autor_foto?: string;
  instituicao_autor?: string;
  SISBIO?: string;
  SISGEN?: string;
  observacoes?: string;
  imagens?: {
    imagem_id: number;
    url_imagem: string;
    status?: string;
    codigo_imagem?: string;
  }[];
  status?: string; // normalized
}

export interface SearchFilters {
  termo?: string; // nome_especie substring
  pais?: string[];
  regiao?: string[];
  estado?: string[];
  municipio?: string[];
  dataInicio?: string;
  dataFim?: string;
  ladoAsa?: "d" | "e" | "all";
  ordenar?: "recent" | "oldest";
  page?: number;
  pageSize?: number;
}

export interface SearchResponse {
  results: SearchResultItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  facets: FacetCounts;
}

// Basic search leveraging /colaboracoes endpoint (backend returns all). Client filters & sort.
export async function searchCollaborations(
  filters: SearchFilters = {}
): Promise<SearchResultItem[]> {
  const r = await api.get("/colaboracoes");
  
  let items: SearchResultItem[] = (r.data || []).map((c: any) => ({
    ...c,
    status: toFrontStatus(c.status),
  }));
  
  // termo (case-insensitive contains)
  if (filters.termo) {
    const t = filters.termo.toLowerCase();
    items = items.filter((i) =>
      (i.nome_especie || "").toLowerCase().includes(t)
    );
  }
  const inList = (val: string | undefined, arr?: string[]) =>
    !arr || arr.length === 0 || (val && arr.includes(val));
  items = items.filter(
    (i) =>
      inList(i.pais, filters.pais) &&
      inList(i.regiao, filters.regiao) &&
      inList(i.estado, filters.estado) &&
      inList(i.municipio, filters.municipio)
  );
  if (filters.dataInicio) {
    items = items.filter((i) => !i.data || i.data >= filters.dataInicio!);
  }
  if (filters.dataFim) {
    items = items.filter((i) => !i.data || i.data <= filters.dataFim!);
  }
  if (filters.ordenar === "recent") {
    items.sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  } else if (filters.ordenar === "oldest") {
    items.sort((a, b) => (a.data || "").localeCompare(b.data || ""));
  }
  return items;
}

export interface FacetCounts {
  pais: Record<string, number>;
  regiao: Record<string, number>;
  estado: Record<string, number>;
  municipio: Record<string, number>;
}

export function buildFacetCounts(items: SearchResultItem[]): FacetCounts {
  const make = () => ({} as Record<string, number>);
  const counts = {
    pais: make(),
    regiao: make(),
    estado: make(),
    municipio: make(),
  };
  for (const i of items) {
    if (i.pais) counts.pais[i.pais] = (counts.pais[i.pais] || 0) + 1;
    if (i.regiao) counts.regiao[i.regiao] = (counts.regiao[i.regiao] || 0) + 1;
    if (i.estado) counts.estado[i.estado] = (counts.estado[i.estado] || 0) + 1;
    if (i.municipio)
      counts.municipio[i.municipio] = (counts.municipio[i.municipio] || 0) + 1;
  }
  return counts;
}

/**
 * Busca server-side usando /search/colaboracoes
 * Processa filtros no servidor e retorna dados paginados com facets
 */
export async function searchCollaborationsV2(
  filters: SearchFilters = {}
): Promise<SearchResponse> {
  const params = new URLSearchParams();

  // Adicionar parâmetros simples
  if (filters.termo) params.append("termo", filters.termo);
  if (filters.dataInicio) params.append("dataInicio", filters.dataInicio);
  if (filters.dataFim) params.append("dataFim", filters.dataFim);
  if (filters.ladoAsa && filters.ladoAsa !== "all")
    params.append("ladoAsa", filters.ladoAsa);
  if (filters.ordenar) params.append("ordenar", filters.ordenar);
  if (filters.page) params.append("page", String(filters.page));
  if (filters.pageSize) params.append("pageSize", String(filters.pageSize));

  // Adicionar arrays de filtros
  filters.pais?.forEach((p) => params.append("pais[]", p));
  filters.regiao?.forEach((r) => params.append("regiao[]", r));
  filters.estado?.forEach((e) => params.append("estado[]", e));
  filters.municipio?.forEach((m) => params.append("municipio[]", m));

  const response = await api.get(
    `/search/colaboracoes?${params.toString()}`
  );

  // Normalizar dados de resposta
  const data = response.data;
  return {
    results: (data.results || []).map((item: any) => ({
      ...item,
      status: toFrontStatus(item.status),
      data: item.data
        ? new Date(item.data).toISOString().split("T")[0]
        : undefined,
    })),
    total: data.total || 0,
    page: data.page || 1,
    pageSize: data.pageSize || 12,
    totalPages: data.totalPages || 1,
    facets: data.facets || { pais: {}, regiao: {}, estado: {}, municipio: {} },
  };
}
